import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  EventLogsIsReadRequest,
  GetEventLogListResponse,
  GetEventLogListV1SrEventLogsGetParams,
  GetTotalIsReadValueResponse,
} from 'api/generated';

export const NOTIFICATIONS_LIST_QUERY_KEY = 'vestra/notifications-list';

export const useIssuerNotificationsListQuery = (
  payload: GetEventLogListV1SrEventLogsGetParams,
  registerId: string,
  disabled = false,
) => {
  const { page, ...data } = payload;
  return useInfiniteQuery<GetEventLogListResponse>({
    queryKey: [NOTIFICATIONS_LIST_QUERY_KEY, JSON.stringify(data), registerId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    retry: 2,
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getEventLogListV1SrEventLogsGet({
        ...data,
        page: pageParam || page,
      });
      return response.data;
    },
    enabled: !disabled,
    keepPreviousData: true,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
  });
};

export const useIssuerNotificationsPageQuery = (
  payload: GetEventLogListV1SrEventLogsGetParams,
  registerId: string,
  disabled = false,
) => {
  return useQuery<GetEventLogListResponse>({
    queryKey: [NOTIFICATIONS_LIST_QUERY_KEY, registerId, JSON.stringify(payload)],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    retry: 2,
    queryFn: async () => {
      const response = await api.v1.getEventLogListV1SrEventLogsGet(payload);
      return response.data;
    },
    enabled: !disabled,
    keepPreviousData: true,
  });
};

export const useIssuerMakeIsReadNotificationsMutation = () => {
  return useMutation({
    mutationFn: async (payload: EventLogsIsReadRequest) => {
      const response = await withErrorHandling(
        api.v1.eventLogIsReadV1SrEventLogsIsReadPatch(payload),
        {
          loading: 'Processing...',
          success: 'Notification marked as read',
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([NOTIFICATIONS_LIST_QUERY_KEY]);
    },
  });
};

export const useIsAllNotificationsRead = (disabled = false) => {
  return useQuery<GetTotalIsReadValueResponse>({
    queryKey: [NOTIFICATIONS_LIST_QUERY_KEY],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    retry: 2,
    queryFn: async () => {
      const response = await api.v1.getTotalIsReadValueV1SrAllIsReadGet({});
      return response.data;
    },
    enabled: !disabled,
    keepPreviousData: true,
  });
};
